import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, KeyRound, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AppShell from "../components/app/AppShell";

function Field({ label, value, onChange, placeholder, type = "text", testId, autoCapitalize }) {
  return (
    <div className="mb-3">
      <label className="label-up">{label}</label>
      <input data-testid={testId} className="input-field" type={type} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} autoCapitalize={autoCapitalize} />
    </div>
  );
}

export default function EditProfile() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [agency, setAgency] = useState(user?.agency || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState("");

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdMsg, setPwdMsg] = useState("");

  if (!user) return null;

  const saveProfile = async () => {
    setProfileMsg(""); setSavingProfile(true);
    try {
      await api("/auth/me", { method: "PATCH", body: { name, agency, phone, email } });
      if (refresh) await refresh();
      setProfileMsg("Saved!"); setTimeout(() => setProfileMsg(""), 2500);
    } catch (e) { alert(e.message); }
    finally { setSavingProfile(false); }
  };

  const changePwd = async () => {
    if (newPwd.length < 6) { setPwdMsg("New password must be at least 6 characters."); return; }
    if (newPwd !== confirmPwd) { setPwdMsg("Passwords don't match."); return; }
    setPwdMsg(""); setSavingPwd(true);
    try {
      await api("/auth/change-password", { method: "POST", body: { current_password: currentPwd, new_password: newPwd } });
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
      setPwdMsg("Password updated!"); setTimeout(() => setPwdMsg(""), 3000);
    } catch (e) { setPwdMsg(e.message || "Could not change password"); }
    finally { setSavingPwd(false); }
  };

  return (
    <AppShell>
      <div className="px-5 lg:px-10 py-8 max-w-2xl">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="w-10 h-10 grid place-items-center -ml-2 rounded-lg hover:bg-[#F5F5F5]">
          <ArrowLeft size={20} />
        </button>
        <span className="eyebrow mt-4 block">YOUR ACCOUNT</span>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-1 mb-6">Edit Profile</h1>

        <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "#E8E8E8" }}>
          <h2 className="font-extrabold mb-4">Profile info</h2>
          <Field label="Display name" value={name} onChange={setName} placeholder="Jane Smith" testId="edit-name" />
          {user.role === "partner" && <Field label="Agency / Uplink" value={agency} onChange={setAgency} placeholder="Acme Insurance" testId="edit-agency" />}
          <Field label="Phone" value={phone} onChange={setPhone} placeholder="(555) 123-4567" type="tel" testId="edit-phone" />
          <Field label="Email (login)" value={email} onChange={setEmail} placeholder="you@email.com" type="email" testId="edit-email" />
          {profileMsg && <p className="text-sm font-bold text-center my-2" style={{ color: "#2E7D32" }}>{profileMsg}</p>}
          <button data-testid="save-profile-btn" onClick={saveProfile} disabled={savingProfile} className="btn-primary w-full !py-3 mt-2">
            {savingProfile ? <Loader2 className="animate-spin" size={16} /> : (<><Save size={16} /> Save Profile</>)}
          </button>
        </div>

        <div className="bg-white rounded-2xl p-5 mt-5 border" style={{ borderColor: "#E8E8E8" }}>
          <h2 className="font-extrabold mb-4">Change password</h2>
          <Field label="Current password" value={currentPwd} onChange={setCurrentPwd} type="password" testId="edit-current-pwd" />
          <Field label="New password" value={newPwd} onChange={setNewPwd} type="password" testId="edit-new-pwd" />
          <Field label="Confirm new password" value={confirmPwd} onChange={setConfirmPwd} type="password" testId="edit-confirm-pwd" />
          {pwdMsg && <p className="text-sm font-bold text-center my-2" style={{ color: pwdMsg === "Password updated!" ? "#2E7D32" : "#C62828" }}>{pwdMsg}</p>}
          <button data-testid="change-pwd-btn" onClick={changePwd} disabled={savingPwd || !currentPwd || !newPwd} className="w-full inline-flex items-center justify-center gap-2 rounded-full font-extrabold text-white text-[15px] py-3 mt-2" style={{ background: "#2E78C5", opacity: savingPwd || !currentPwd || !newPwd ? 0.6 : 1 }}>
            {savingPwd ? <Loader2 className="animate-spin" size={16} /> : (<><KeyRound size={16} /> Update Password</>)}
          </button>
        </div>
      </div>
    </AppShell>
  );
}
